import { Component, ChangeDetectionStrategy } from "@angular/core";
import { Session } from "@core/models";
import {
  refreshFromToken,
  selectIsSessionSwisscomHealthOrgAdminWrite,
  selectSession
} from "@core/store";
import { selectHeaderNavigationData } from "@organizations/store";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Observable } from "rxjs";
import { CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HeaderComponent {
  public session$: Observable<Session>;

  login: string = "{}";
  navigation: string = "{}";
  index: string = JSON.stringify({
    label: "Home",
    href: window.location.origin,
    ariaLabel: "Home",
    rel: "nofollow"
  });
  isReady = false;

  constructor(
    private translateService: TranslateService,
    private store: Store
  ) {
    // Refresh session from token if available
    // We need this because the msal guard does not inform us of silently acquired tokens
    this.store.dispatch(refreshFromToken());

    this.session$ = this.store.select(selectSession);

    combineLatest([
      this.session$,
      this.store.select(selectIsSessionSwisscomHealthOrgAdminWrite)
    ]).subscribe(([session, isAdmin]) => {
      console.log("isAdmin", isAdmin);
      if (!session) {
        this.isReady = false;
        return;
      }
      const givenName = session.givenName;
      const familyName = session.familyName;
      if (givenName && familyName) {
        this.buildLogin(session.isLoggedIn, givenName, familyName, isAdmin);
      } else {
        this.buildLogin(session.isLoggedIn, session.email, "", isAdmin);
      }
      this.buildNavigation();
      this.isReady = true;
    });
  }

  private buildLogin(isLoggedIn: boolean, givenName = "", familyName = "", isAdmin = false) {
    this.translateService
      .get(["header.label_login", "header.label_logout", "header.label_admin"])
      .subscribe((translations) => {
        if (isLoggedIn) {
          this.login = JSON.stringify({
            slot: "login",
            customer: {
              fullName: `${givenName} ${familyName}`
            },
            badge: isAdmin ? translations["header.label_admin"] : "",
            logout: {
              label: translations["header.label_logout"],
              href: "/logout"
            }
          });
        } else {
          this.login = JSON.stringify({});
        }
      });
  }

  private buildNavigation() {
    combineLatest([
      this.session$,
      this.store.select(selectHeaderNavigationData),
      this.translateService.get([
        "header.main.label",
        "header.main.label_my_account",
        "header.main.label_organizations",
        "header.main.label_users"
      ])
    ]).subscribe(([session, { organization, isAdmin }, results]) => {
      const navigation = {
        main: {
          label: results["header.main.label"],
          children: session?.isLoggedIn
            ? [
                {
                  label: results["header.main.label_my_account"],
                  href: "/my-account",
                  active: window.location.pathname.startsWith("/my-account")
                },
                {
                  label: results["header.main.label_organizations"],
                  href: "/organizations",
                  active: window.location.pathname.startsWith("/organizations"),
                  children: organization
                    ? [
                        {
                          label: organization?.name,
                          active: true,
                          hidden: true
                        }
                      ]
                    : []
                },
                // if isAdmin is true
                ...(isAdmin
                  ? [
                      {
                        label: results["header.main.label_users"],
                        href: "/users",
                        active: window.location.pathname.startsWith("/users")
                      }
                    ]
                  : [])
              ]
            : []
        }
      };
      this.navigation = JSON.stringify(navigation);
    });
  }
}
